import { useEffect, useState } from 'react'
import { TOOLTIP } from '../../../../../constants/tooltips'
import { WellSummaryDetailsView } from '../../../../../types/form-types'
import { useGetWellData } from '../../../../hooks/use-api'
import { useWellId } from '../../../../hooks/use-well-id'
import {
  Divider,
  FormField,
  FormFieldRow,
  Heading,
  InputWidth,
  ReadOnlyField,
  RecordContent,
  useTitleChange,
} from '@msaf/core-react'

export default function ViewWellSummaryDetails() {
  useTitleChange('View Well | Well summary')

  const [fields, setFields] = useState<Partial<WellSummaryDetailsView>>({})
  const id = useWellId()
  const url = `wells/${id}/summary-details/view`
  const { isLoading, isSuccess, data } = useGetWellData(url)

  useEffect(() => {
    if (isSuccess && data?.state) {
      setFields(data.state)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, data?.state])

  return (
    <RecordContent>
      <Heading level={2}>Summary</Heading>
      <FormField isSkeleton={isLoading} labelId='wellType' label='Type'>
        <ReadOnlyField isSkeleton={isLoading} labelledBy='wellType' value={fields?.wellType ?? ''} />
      </FormField>
      <FormField isSkeleton={isLoading} labelId='wellStatus' label='Status'>
        <ReadOnlyField isSkeleton={isLoading} labelledBy='wellType' value={fields?.wellStatus ?? ''} />
      </FormField>
      <FormField isSkeleton={isLoading} labelId='wellUses' label='Use(s)'>
        <ReadOnlyField isSkeleton={isLoading} labelledBy='wellUses' value={fields?.wellUses ?? ''} />
      </FormField>
      <Divider />
      <FormField isSkeleton={isLoading} labelId='wellAddress' label='Address'>
        <ReadOnlyField isSkeleton={isLoading} labelledBy='wellAddress' value={fields?.wellAddress ?? ''} />
      </FormField>
      <FormField isSkeleton={isLoading} labelId='wellLocality' label='Locality'>
        <ReadOnlyField isSkeleton={isLoading} labelledBy='wellLocality' value={fields?.locality ?? ''} />
      </FormField>
      {/* If SME feedback is latitude / longitude required commit 055aed35 has client code Lat / Lng should already be sent from server*/}

      <FormFieldRow>
        <FormField isSkeleton={isLoading} labelId='easting' label='NZTM Easting'>
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy='easting'
            value={fields?.easting ?? ''}
            fieldSize={InputWidth.large}
          />
        </FormField>
        <FormField isSkeleton={isLoading} labelId='northing' label='NZTM Northing'>
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy='northing'
            value={fields?.northing ?? ''}
            fieldSize={InputWidth.large}
          />
        </FormField>
      </FormFieldRow>
      <Divider />
      <FormField isSkeleton={isLoading} labelId='drillingCompany' label='Drilling company'>
        <ReadOnlyField isSkeleton={isLoading} labelledBy='drillingCompany' value={fields?.drillingCompany ?? ''} />
      </FormField>
      <FormField isSkeleton={isLoading} labelId='drillingDate' label='Drilling date'>
        <ReadOnlyField
          isSkeleton={isLoading}
          labelledBy='drillingDate'
          value={fields?.drillingDateFormatted ?? ''}
          fieldSize={InputWidth.medium}
        />
      </FormField>
      <FormFieldRow>
        <FormField
          toolTipMessage={TOOLTIP.maxDepthDrilled}
          isSkeleton={isLoading}
          labelId='drillingDepth'
          label='Drilling depth (m)'
        >
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy='drillingDepth'
            value={fields?.actualDepthDrilled ?? ''}
            fieldSize={InputWidth.medium}
          />
        </FormField>
      </FormFieldRow>
      <FormFieldRow>
        <FormField
          toolTipMessage={TOOLTIP.actualMaxDiameter}
          isSkeleton={isLoading}
          labelId='maxDiameter'
          label='Max diameter (mm)'
        >
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy='maxDiameter'
            value={fields?.actualMaxDiameter ?? ''}
            fieldSize={InputWidth.medium}
          />
        </FormField>
      </FormFieldRow>
      <Divider />
      <FormField isSkeleton={isLoading} labelId='screenCount' label='Number of screens'>
        <ReadOnlyField
          isSkeleton={isLoading}
          labelledBy='screenCount'
          value={fields?.screenCount ?? ''}
          fieldSize={InputWidth.medium}
        />
      </FormField>
      <FormFieldRow>
        <FormField isSkeleton={isLoading} labelId='topScreenTop' label='Top of top screen'>
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy='topScreenTop'
            value={fields?.firstScreenTop ?? ''}
            fieldSize={InputWidth.large}
          />
        </FormField>
        <FormField isSkeleton={isLoading} labelId='bottomScreenBottom' label='Bottom of bottom screen'>
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy='bottomScreenBottom'
            value={fields?.lastScreenBottom ?? ''}
            fieldSize={InputWidth.large}
          />
        </FormField>
      </FormFieldRow>
      <FormField toolTipMessage={TOOLTIP.aquiferName} isSkeleton={isLoading} labelId='aquiferName' label='Aquifer'>
        <ReadOnlyField isSkeleton={isLoading} labelledBy='aquiferName' value={fields?.aquiferName ?? ''} />
      </FormField>
      <Divider />
      <FormField isSkeleton={isLoading} labelId='elvation' label='Elevation (NZ Vertical)'>
        <ReadOnlyField isSkeleton={isLoading} labelledBy='elevation' value={fields?.nzvdElevation ?? ''} />
      </FormField>
      <FormField isSkeleton={isLoading} labelId='groundLevelCorrection' label='Measuring point from GL'>
        <ReadOnlyField
          isSkeleton={isLoading}
          labelledBy='groundLevelCorrection'
          value={fields?.groundLevelCorrection ?? ''}
        />
      </FormField>
      <FormField isSkeleton={isLoading} labelId='wellheadSecurityRating' label='Overall wellhead security rating'>
        <ReadOnlyField
          isSkeleton={isLoading}
          labelledBy='wellheadSecurityRating'
          value={fields?.wellheadSecurityRating ?? ''}
        />
      </FormField>
      <Divider />
      <FormFieldRow>
        <FormField isSkeleton={isLoading} labelId='numberLithology' label='Number of lithology records'>
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy='numberLithology'
            value={fields?.strataCount ?? ''}
            fieldSize={InputWidth.large}
          />
        </FormField>
        <FormField
          isSkeleton={isLoading}
          labelId='yieldDrawdownTestStepCount'
          label='Number of yield drawdown test steps'
        >
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy='yieldDrawdownTestStepCount'
            value={fields?.yieldDrawdownTestStepCount ?? ''}
            fieldSize={InputWidth.large}
          />
        </FormField>
      </FormFieldRow>
      <FormFieldRow>
        <FormField isSkeleton={isLoading} labelId='numberDocuments' label='Number of documents'>
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy='numberDocuments'
            value={fields?.documentCount ?? ''}
            fieldSize={InputWidth.large}
          />
        </FormField>
        <FormField isSkeleton={isLoading} labelId='numberAquiferTests' label='Number of aquifer tests'>
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy='numberAquiferTests'
            value={fields?.aquiferTestCount ?? ''}
            fieldSize={InputWidth.large}
          />
        </FormField>
      </FormFieldRow>
    </RecordContent>
  )
}
